import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dropdown, Form, message } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCanCheck } from '@/ability/helpers';
import StatusBadge from '@/components/ui/StatusBadge';
import {
    getSchedules,
    getScheduleById,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    assignUsersToSchedule,
} from '../services/schedule.service';
import { getEmployees } from '../../employee/services/employee.service';
import { SCHEDULES_QUERY_KEY, SCHEDULE_DETAIL_QUERY_KEY } from './schedulesQueryKeys';
import { getScheduleColumns } from '../columns/scheduleColumns';

const STATUS_TAB = { ALL: 'all', ACTIVE: 'active', INACTIVE: 'inactive' };

const DAYS_MAP = {
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
};

const formatWorkingDays = (days) => {
    if (!Array.isArray(days) || days.length === 0) return '—';
    return days.map(d => DAYS_MAP[d.toLowerCase()] || d).join(', ');
};

export const useScheduleDetail = (id) =>
    useQuery({
        queryKey: SCHEDULE_DETAIL_QUERY_KEY(id),
        queryFn: () => getScheduleById(id),
        enabled: !!id,
    });

export const useSchedules = () => {
    const queryClient = useQueryClient();
    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();

    const [searchText, setSearchText] = useState('');
    const [statusTab, setStatusTab] = useState(STATUS_TAB.ALL);

    const [panelOpen, setPanelOpen] = useState(false);
    const [selectedScheduleId, setSelectedScheduleId] = useState(null);

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const [scheduleForEdit, setScheduleForEdit] = useState(null);
    const [scheduleForDelete, setScheduleForDelete] = useState(null);

    const can = useCanCheck();
    const canView = can('hr.schedule.view');
    const canCreate = can('hr.schedule.create');
    const canUpdate = can('hr.schedule.update');
    const canDelete = can('hr.schedule.delete');

    const { data: response, isLoading, isError, refetch } = useQuery({
        queryKey: [...SCHEDULES_QUERY_KEY, { page: 1, limit: 100 }],
        queryFn: () => getSchedules({ page: 1, limit: 100 }),
        enabled: !!canView,
        retry: false,
    });

    const createScheduleMutation = useMutation({
        mutationFn: (body) => createSchedule(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SCHEDULES_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] });
        },
    });

    const updateScheduleMutation = useMutation({
        mutationFn: ({ id, body }) => updateSchedule(id, body),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: SCHEDULES_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: SCHEDULE_DETAIL_QUERY_KEY(id) });
            queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] });
        },
    });

    const deleteScheduleMutation = useMutation({
        mutationFn: (id) => deleteSchedule(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SCHEDULES_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] });
        },
    });

    const assignUsersMutation = useMutation({
        mutationFn: ({ id, body }) => assignUsersToSchedule(id, body),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: SCHEDULES_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: SCHEDULE_DETAIL_QUERY_KEY(id) });
            queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] });
        },
    });

    const schedules = response?.data ?? [];
    const total = response?.total ?? 0;
    const activeCount = useMemo(
        () => schedules.filter((s) => (s.status || '').toLowerCase() === 'active').length,
        [schedules],
    );

    const selectedScheduleName = useMemo(
        () => schedules.find((s) => s.id === selectedScheduleId)?.title ?? 'Schedule details',
        [schedules, selectedScheduleId],
    );

    const filteredSchedules = useMemo(() => {
        let list = schedules;
        if (statusTab === STATUS_TAB.ACTIVE) {
            list = list.filter((s) => (s.status || '').toLowerCase() === 'active');
        } else if (statusTab === STATUS_TAB.INACTIVE) {
            list = list.filter((s) => (s.status || '').toLowerCase() !== 'active');
        }

        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter((s) => (s.title || '').toLowerCase().includes(q));
        }
        return list;
    }, [schedules, statusTab, searchText]);

    const tableData = useMemo(
        () =>
            filteredSchedules.map((s) => ({
                key: s.id,
                id: s.id,
                title: s.title,
                startTime: s.startTime,
                endTime: s.endTime,
                workingDays: s.workingDays,
                graceTime: s.graceTime,
                calendarEndDay: s.calendarEndDay,
                salaryType: s.salaryType ?? 'fixed',
                absentDeductionType: s.absentDeductionType ?? 'per_day',
                fixedAbsentDeductionAmount: s.fixedAbsentDeductionAmount ?? '0.00',
                status: s.status,
                userCount: s.userCount,
            })),
        [filteredSchedules],
    );

    const handleActionClick = (menuInfo, record) => {
        if (menuInfo.key === 'edit') {
            setScheduleForEdit(record);
            editForm.setFieldsValue({
                title: record.title,
                startTime: record.startTime ? dayjs(`2000-01-01T${record.startTime}`) : undefined,
                endTime: record.endTime ? dayjs(`2000-01-01T${record.endTime}`) : undefined,
                workingDays: record.workingDays,
                graceTime: record.graceTime,
                calendarEndDay: record.calendarEndDay ?? 1,
                salaryType: record.salaryType ?? 'fixed',
                absentDeductionType: record.absentDeductionType ?? 'per_day',
                fixedAbsentDeductionAmount: record.fixedAbsentDeductionAmount ? parseFloat(record.fixedAbsentDeductionAmount) : 0,
                status: record.status,
                userIds: [],
            });

            getScheduleById(record.id).then((full) => {
                const userIds = Array.isArray(full?.users) ? full.users.map((u) => u.id) : [];
                editForm.setFieldsValue({ userIds });
            }).catch(() => {});

            setEditModalOpen(true);
        }
        if (menuInfo.key === 'delete') {
            setScheduleForDelete(record);
            setDeleteConfirmOpen(true);
        }
        if (menuInfo.key === 'view') {
            openPanel(record.id);
        }
    };

    const openPanel = (id) => {
        setSelectedScheduleId(id);
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setSelectedScheduleId(null);
    };

    const closeCreateModal = useCallback(() => {
        setCreateModalOpen(false);
        createForm.resetFields();
    }, [createForm]);

    const onSubmitCreate = useCallback(() => {
        createForm.validateFields().then((values) => {
            const payload = {
                ...values,
                startTime: values.startTime ? dayjs(values.startTime).format('HH:mm') : undefined,
                endTime: values.endTime ? dayjs(values.endTime).format('HH:mm') : undefined,
            };
            createScheduleMutation.mutate(payload, {
                onSuccess: () => {
                    message.success('Schedule created successfully.');
                    closeCreateModal();
                },
                onError: (err) => {
                    message.error(err?.response?.data?.message || 'Failed to create schedule.');
                },
            });
        });
    }, [createForm, createScheduleMutation, closeCreateModal]);

    const closeEditModal = useCallback(() => {
        setEditModalOpen(false);
        setScheduleForEdit(null);
        editForm.resetFields();
    }, [editForm]);

    const onSubmitEdit = useCallback(() => {
        if (!scheduleForEdit?.id) return;
        editForm.validateFields().then(async (values) => {
            const payload = {
                ...values,
                startTime: values.startTime ? dayjs(values.startTime).format('HH:mm') : undefined,
                endTime: values.endTime ? dayjs(values.endTime).format('HH:mm') : undefined,
            };
            try {
                await updateSchedule(scheduleForEdit.id, payload);
                if (Array.isArray(values.userIds)) {
                    await assignUsersToSchedule(scheduleForEdit.id, values.userIds);
                }
                message.success('Schedule updated successfully.');
                queryClient.invalidateQueries({ queryKey: SCHEDULES_QUERY_KEY });
                closeEditModal();
            } catch (err) {
                message.error(err?.response?.data?.message || 'Failed to update schedule.');
            }
        });
    }, [scheduleForEdit, editForm, closeEditModal, queryClient]);

    const confirmDelete = useCallback(() => {
        if (!scheduleForDelete?.id) return;
        deleteScheduleMutation.mutate(scheduleForDelete.id, {
            onSuccess: () => {
                message.success('Schedule deleted successfully.');
                setDeleteConfirmOpen(false);
                setScheduleForDelete(null);
            },
            onError: (err) => {
                message.error(err?.response?.data?.message || 'Failed to delete schedule.');
            },
        });
    }, [scheduleForDelete, deleteScheduleMutation]);

    const columns = useMemo(
        () =>
            getScheduleColumns({
                openPanel,
                handleMenuClick: (key, record) => handleActionClick({ key }, record),
                canUpdate,
                canDelete,
            }),
        [openPanel, handleActionClick, canUpdate, canDelete],
    );

    const { data: employeesResponse } = useQuery({
        queryKey: ['hr', 'employees', 'list'],
        queryFn: () => getEmployees({ page: 1, limit: 100 }),
    });
    const employees = employeesResponse?.data ?? [];
    const employeeOptions = useMemo(() => {
        return employees.map((e) => ({
            value: e.id,
            label: e.name ? `${e.name} (${e.email})` : e.email,
        }));
    }, [employees]);

    const totalAssignedEmployees = useMemo(() => {
        return tableData.reduce((sum, item) => sum + (item.userCount ?? 0), 0);
    }, [tableData]);

    return {
        schedules,
        total,
        activeCount,
        totalAssignedEmployees,
        employeeOptions,
        filteredSchedules,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        // panel state
        panelOpen,
        selectedScheduleId,
        selectedScheduleName,
        openPanel,
        closePanel,
        // create modal
        canCreate,
        createModalOpen,
        setCreateModalOpen,
        createForm,
        onSubmitCreate,
        closeCreateModal,
        createSchedulePending: createScheduleMutation.isPending,
        // edit modal
        canUpdate,
        editModalOpen,
        setEditModalOpen,
        editForm,
        onSubmitEdit,
        closeEditModal,
        updateSchedulePending: updateScheduleMutation.isPending,
        scheduleForEdit,
        // delete confirm
        canDelete,
        deleteConfirmOpen,
        setDeleteConfirmOpen,
        confirmDelete,
        deleteSchedulePending: deleteScheduleMutation.isPending,
        scheduleForDelete,
        // assign users
        assignUsersMutation,
        // permissions
        canView,
        isLoading,
        isError,
        refetch,
    };
};
